// Library Imports
import { History } from "history";

// Local Imports
import {
  emailsService,
  eventsService,
  listsService,
  updateWorkflowService,
  createWorkflow,
  getControls,
  getWorkflowDetailsService,
  getUserWorkflowsService,
  deleteWorkflowService,
  emailMappingService,
} from "modules/Services/AutomationServices";
import store from "store";
import {
  AUTOMATION_EMAILS,
  AUTOMATION_EVENTS,
  AUTOMATION_DROP_ITEM,
  AUTOMATION_MODAL,
  CREATE_AUTOMATION,
  AUTOMATION_NODE,
  AUTOMATION_LISTS,
  AUTOMATION_NAME_UPDATE,
  AUTOMATION_CONTROLS,
  AUTOMATION_WORKFLOW,
  AUTOMATION_USER_WORKFLOWS,
  AUTOMATION_USER_WORKFLOWS_DELETE,
  AUTOMATION_WORKFLOW_STATUS_UPDATE,
  AUTOMATION_WORKFLOW_IF_ELSE_MODAL,
} from "../../constants/types/automationTypes";
import { ControlNameEnum, ControlTypeEnum, WFStatusEnum } from "./interfaces";
import { routes } from "./constants";

// Actions
export const addActionModal = (flag: boolean, id: string = "", tab: string = "") => {
  return {
    type: AUTOMATION_MODAL.ADD_ACTION,
    payload: { flag, id, tab },
  };
};

export const deleteActionModal = (
  flag: boolean,
  id: string,
  index?: number,
  changeDeleteObject: boolean = true
) => {
  return {
    type: AUTOMATION_MODAL.DELETE_ACTION,
    payload: { flag, id, index, changeDeleteObject },
  };
};

export const deleteConditionModal = (flag: boolean, id: string) => {
  return {
    type: AUTOMATION_MODAL.DELETE_CONDITION,
    payload: { flag, id },
  };
};

export const conditionConstraintModal = (flag: boolean) => {
  return {
    type: AUTOMATION_MODAL.CONDITION_CONSTRAINT,
    payload: { flag },
  };
};

export const setPublishModal = (flag: boolean) => {
  return {
    type: AUTOMATION_MODAL.SHOW_PUBLISH_MESSAGE,
    payload: { flag },
  };
};

export const setInactiveModal = (flag: boolean) => {
  return {
    type: AUTOMATION_MODAL.INACTIVE,
    payload: { flag },
  };
};

export const addNode = (payload: any) => {
  return {
    type: CREATE_AUTOMATION.ADD_NODE,
    payload,
  };
};

export const publishWorkflow = (onlySave?: boolean) => {
  const { automation } = store.getState();
  const workflow = automation?.workflow?.flow;
  const workflowId = automation?.workflow?.detail?.id;
  let data: { [key: string]: any } = {
    workflowId,
    workflow,
  };
  if (!onlySave) {
    data["status"] = WFStatusEnum.ACTIVE;
  }
  updatedWorkflowAPI(data);
};

export const eventsAPI = (searchTerm?: string) => {
  store.dispatch({
    type: AUTOMATION_EVENTS.LOAD,
  });
  eventsService(searchTerm)
    .then((res) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_EVENTS.LOAD_SUCCESS,
          events: res.data.data,
          isSearched: searchTerm,
        });
      } else {
        store.dispatch({ type: AUTOMATION_EVENTS.LOAD_FAILED });
      }
    })
    .catch((err) => {
      store.dispatch({ type: AUTOMATION_EVENTS.LOAD_FAILED });
    });
};

export const emailsAPI = (params: any) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.LOAD,
    payload: { resetPage: params.offset === 0 },
  });
  if (params?.email_type === "2") delete params.email_type;
  emailsService(params)
    .then((res) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_EMAILS.LOAD_SUCCESS,
          emails: res.data.data.emails,
          totalCount: res.data.emails_total_count,
          paginatedCall: params.offset === 0 ? false : true,
          shareProfileTemplates: res.data.data.templates,
        });
      } else {
        store.dispatch({ type: AUTOMATION_EMAILS.LOAD_FAILED });
      }
    })
    .catch((err) => {
      console.error("emailAPI error: ", err);
      if (err?.response && !err?.response?.message?.includes("Operation canceled due to new request")) {
        store.dispatch({ type: AUTOMATION_EMAILS.LOAD_FAILED });
      }
    });
};

export const resetWorkflowEmails = () => {
  store.dispatch({
    type: AUTOMATION_EMAILS.RESET,
  });
};

export const saveDropArea = (payload: any) => {
  return {
    type: AUTOMATION_DROP_ITEM.SAVE,
    payload,
  };
};

export const clearDropArea = () => {
  return {
    type: AUTOMATION_DROP_ITEM.CLEAR,
  };
};

export const deleteNode = (id: string, index: number) => {
  return {
    type: AUTOMATION_NODE.DELETE,
    payload: { id, index },
  };
};

export const copyNode = (payload: string) => {
  return {
    type: AUTOMATION_NODE.COPY,
    payload,
  };
};

export const clearCopyNode = () => {
  return {
    type: AUTOMATION_NODE.CLEAR_COPY,
  };
};

export const moveNode = (payload: string) => {
  return {
    type: AUTOMATION_NODE.MOVE,
    payload,
  };
};

export const editNode = (id: string, data: object) => {
  return {
    type: AUTOMATION_NODE.EDIT,
    payload: { id, data },
  };
};

export const resetWorkflow = () => {
  return {
    type: AUTOMATION_WORKFLOW.RESET,
  };
};

export const addWFEmail = (payload: number) => {
  return {
    type: AUTOMATION_WORKFLOW.ADD_EMAIL,
    payload,
  };
};

export const removeWFEmail = (payload: number) => {
  return {
    type: AUTOMATION_WORKFLOW.REMOVE_EMAIL,
    payload,
  };
};

export const listsAPI = (params: any) => {
  let searchTerm = params?.list_name;
  store.dispatch({
    type: AUTOMATION_LISTS.LOAD,
  });
  listsService(params)
    .then((res) => {
      if (res.data.success) {
        store.dispatch({
          type: searchTerm ? AUTOMATION_LISTS.SET_SEARCH_DATA : AUTOMATION_LISTS.LOAD_SUCCESS,
          lists: res.data.data,
          totalCount: res.data.total_count,
        });
      } else {
        store.dispatch({ type: AUTOMATION_LISTS.LOAD_FAILED });
      }
    })
    .catch((err) => {
      if (err?.response && !err?.response?.message?.includes("Operation canceled due to new request")) {
        store.dispatch({ type: AUTOMATION_LISTS.LOAD_FAILED });
      }
    });
};

export const nameUpdateHandler = (payload: string, workflowId: number) => {
  const obj = { workflow_name: payload, workflow_id: workflowId };
  store.dispatch({
    type: AUTOMATION_NAME_UPDATE.LOAD,
  });
  updateWorkflowService(obj)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_NAME_UPDATE.SUCCESS,
          payload,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_NAME_UPDATE.FAIL,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_NAME_UPDATE.FAIL,
      });
    });
};

export const getControlsAPI = () => {
  getControls()
    .then((res) => {
      const data = res.data.data;
      let lastItem = data.action.pop();
      let arr = [
        ...data.action,
        {
          id: 3,
          name: ControlNameEnum.IF_ELSE,
          title: ControlNameEnum.IF_ELSE,
          type: ControlTypeEnum.CONDITION,
          conditions: [...data.condition],
        },
        lastItem,
      ];
      store.dispatch({
        type: AUTOMATION_CONTROLS.SAVE,
        payload: arr,
      });
    })
    .catch((error) => {});
};

export const createWorkflowAPI = (emailId: number = 0, triggerId: number = 0, history: History) => {
  store.dispatch({
    type: CREATE_AUTOMATION.LOADING,
  });
  createWorkflow(emailId, triggerId)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: CREATE_AUTOMATION.SAVE,
          payload: res.data.data,
        });
        history.push(`${routes.DETAIL}?workflow_id=${res.data.data.id}`);
      } else {
        store.dispatch({
          type: CREATE_AUTOMATION.FAIL,
        });
        console.log("error");
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: CREATE_AUTOMATION.FAIL,
      });
      console.log(err);
    });
};

export const updatedWorkflowAPI = (payload: any, isAutoSave?: boolean) => {
  // Object.keys(payload.workflow).forEach(function (key) {
  //   delete payload.workflow[key]?.detail?.nodeTitle;
  //   delete payload.workflow[key]?.detail?.nodeId;
  // });

  const { workflowId, workflow, status } = payload;

  let obj = {
    workflow_id: workflowId,
    ...(workflow && { workflow: JSON.stringify(workflow) }),
    ...(status && { status }),
  };

  if (!isAutoSave) {
    store.dispatch({
      type: CREATE_AUTOMATION.LOADING,
    });
  }

  updateWorkflowService(obj)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: CREATE_AUTOMATION.UPDATE,
          payload: { status },
        });
      } else {
        store.dispatch({
          type: CREATE_AUTOMATION.FAIL,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: CREATE_AUTOMATION.FAIL,
      });
    });

  emailMappingAPI(workflowId);
};

export const resetSuccessMessage = () => {
  store.dispatch({
    type: CREATE_AUTOMATION.RESET_MESSAGE,
  });
};

export const getWorkflowDetails = (workflowId: number) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW.LOAD,
  });
  getWorkflowDetailsService(workflowId)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW.LOAD_SUCCESS,
          payload: res.data.data,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_WORKFLOW.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const getUserWorkflows = (offset: number, limit: number, workflowName?: string, filterType?: string) => {
  store.dispatch({
    type: AUTOMATION_USER_WORKFLOWS.LOAD,
    payload: { offset },
  });
  getUserWorkflowsService(offset, limit, workflowName, filterType)
    .then((res: any) => {
      if (res.data.success) {
        if (Boolean(workflowName)) {
          store.dispatch({
            type: AUTOMATION_USER_WORKFLOWS.SET_SEARCH_TERM,
            payload: workflowName,
          });
        }
        if (Boolean(filterType)) {
          store.dispatch({
            type: AUTOMATION_USER_WORKFLOWS.SET_FILTER_TYPE,
            payload: filterType,
          });
        }
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.LOAD_SUCCESS,
          userWorkflows: res.data.data,
          isSearched: Boolean(workflowName) || Boolean(filterType),
          totalCount: res.data.total_count,
          paginatedCall: offset === 0 ? false : true,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const resetUserWorkflows = () => {
  store.dispatch({
    type: AUTOMATION_USER_WORKFLOWS.RESET,
  });
};

export const deleteWorkflow = (
  workflowIds: number[],
  isSearched: boolean,
  selectAll: boolean,
  excludedWorkflowIds: number[],
  searchTerm: string,
  filterType: string
) => {
  store.dispatch({
    type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD,
  });

  deleteWorkflowService(workflowIds, selectAll, excludedWorkflowIds, searchTerm, filterType)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_SUCCESS,
          payload: workflowIds,
          totalCount: res.data.total_count,
          isSearched: isSearched,
        });
      } else {
        store.dispatch({
          type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_FAILED,
      });
      console.log(err);
    });
};

export const updateWorkflowStatusAPI = (workflowId: number, workflowStatus: string) => {
  const updatedWorkflowObj = { workflow_id: workflowId, status: workflowStatus };
  store.dispatch({
    type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD,
  });
  updateWorkflowService(updatedWorkflowObj)
    .then((res: any) => {
      if (res.data.success) {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_SUCCESS,
          payload: { workflowId, workflowStatus },
        });
      } else {
        store.dispatch({
          type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_FAILED,
        });
      }
    })
    .catch((err: any) => {
      store.dispatch({
        type: AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_FAILED,
      });
    });
};

export const emailMappingAPI = (workflowId: number) => {
  const { addEmails, removeEmails } = store.getState().automation.workflow;
  if (addEmails.length || removeEmails.length) {
    // if both the arrays are empty then API request should not go from the frontend
    const emailsObj = {
      workflow_id: workflowId,
      ...(addEmails.length && { add_emails: addEmails }),
      ...(removeEmails.length && { remove_emails: removeEmails }),
    };
    emailMappingService(emailsObj)
      .then((res: any) => {
        if (res.data.success) {
          store.dispatch({
            type: AUTOMATION_WORKFLOW.RESET_EMAILS,
          });
        }
      })
      .catch(() => {
        store.dispatch({
          type: AUTOMATION_WORKFLOW.LOAD_FAILED,
        });
      });
  }
};

export const updateToasterMessage = (message: string) => {
  return {
    type: CREATE_AUTOMATION.UPDATE_TOASTER_MESSAGE,
    payload: { message },
  };
};

export const setSelectedEmailIdAction = (email_id: number) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.SET_SELECTED_EMAIL_ID,
    payload: { email_id },
  });
};

export const setSelectedlistIdsAction = (list_ids: number[]) => {
  store.dispatch({
    type: AUTOMATION_LISTS.SET_SELECTED_LIST_IDS,
    payload: { list_ids },
  });
};

export const setSelectedIfElsesAction = (conditionId: number, conditionEmailId: number) => {
  store.dispatch({
    type: AUTOMATION_WORKFLOW_IF_ELSE_MODAL.SET_CONDITION_EMAIL,
    payload: { conditionId, conditionEmailId },
  });
};

export const updateFilterInStore = (value: string) => {
  store.dispatch({
    type: AUTOMATION_EMAILS.SET_FILTERS,
    payload: value,
  });
};
